import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { PENDING_CHECKOUT_KEY } from '../components/UpgradeModal';

// Gæste-checkout-flow deferrer plan-valget her indtil brugeren har oprettet
// konto / logget ind. Denne side:
//   1. Venter på AuthContext.
//   2. Hvis stadig anonym → /login?next=/billing/resume-checkout (bevar planen).
//   3. Læser localStorage.pending_checkout — hvis mangler eller ældre end
//      30 minutter → ryd og send bruger til /settings.
//   4. POSTer /api/billing/checkout(plan) og redirecter til Stripe.
//
// Loggede brugere når normalt IKKE denne side; UpgradeModal springer den
// helt over. Den fungerer også som robust fallback hvis nogen deep-linker.

const PENDING_MAX_AGE_MS = 30 * 60 * 1000; // 30 min

const readPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.plan || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > PENDING_MAX_AGE_MS) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
};

const clearPending = () => {
  try { localStorage.removeItem(PENDING_CHECKOUT_KEY); } catch (_e) { /* noop */ }
};

const BillingResumeCheckoutPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;

    // Ikke logget ind endnu — send til login og bevar planen.
    if (!user) {
      navigate('/login?mode=login&next=/billing/resume-checkout', { replace: true });
      return;
    }

    const pending = readPending();
    if (!pending) {
      // Intet gemt plan-valg (eller udløbet) → tag brugeren til settings hvor
      // de kan starte forfra via SubscriptionSection.
      clearPending();
      navigate('/settings', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        const { data } = await axios.post(
          `${API}/billing/checkout`,
          { plan: pending.plan, origin_url: window.location.origin },
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
        );
        if (cancelled) return;
        clearPending();
        if (data?.url) {
          window.location.replace(data.url);
        } else {
          setError('Kunne ikke starte betalingen — prøv igen fra dine indstillinger.');
        }
      } catch (e) {
        if (cancelled) return;
        clearPending();
        setError(e.response?.data?.detail || 'Kunne ikke starte betalingen.');
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="billing-resume-checkout-page">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {!error ? (
          <>
            <FaSpinner className="animate-spin text-4xl text-cyan-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Sender dig videre til betaling…</h1>
            <p className="text-sm text-gray-500">Vi fortsætter med det abonnement du valgte inden du oprettede kontoen.</p>
          </>
        ) : (
          <>
            <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Vi kunne ikke starte betalingen</h1>
            <p className="text-sm text-gray-600 mb-5">{error}</p>
            <button
              onClick={() => navigate('/settings')}
              className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-purple-600"
              data-testid="resume-checkout-back-to-settings-btn"
            >
              Tilbage til indstillinger
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingResumeCheckoutPage;
